// 里程碑管理 Hook - useMilestone.ts
// 管理里程碑进度状态和计算逻辑

import { ref, type Ref } from 'vue';

// 类型定义
interface MilestoneConfig {
  serverTimestamp?: Ref<number>; // 响应式的服务器时间戳
}

/**
 * 里程碑时间节点配置 (UTC+8)
 * @returns {number[]} 时间戳数组（秒级）
 */
const getMilestoneTimestamps = (): number[] => {
  const dateTimeConfigs = [
    '2026-03-19T10:00:00+08:00', // 第1个节点: 2026/3/19 10:00 (UTC+8)
    '2026-03-20T00:00:00+08:00', // 第2个节点: 2026/3/20 0:00 (UTC+8)
    '2026-03-21T12:00:00+08:00', // 第3个节点: 2026/3/21 12:00 (UTC+8)
    '2026-03-23T12:00:00+08:00', // 第4个节点: 2026/3/23 12:00 (UTC+8)
    '2026-03-25T00:00:00+08:00', // 第5个节点: 2026/3/25 0:00 (UTC+8)
  ];

  return dateTimeConfigs.map((dateTimeStr) => {
    const date = new Date(dateTimeStr);
    return Math.floor(date.getTime() / 1000); // 转换为秒级时间戳
  });
};

/**
 * 根据当前时间戳计算达到的里程碑节点数量
 * @param {number} currentTimestamp - 当前时间戳（秒）
 * @returns {number} 达到的里程碑节点数量 (0-5)
 */
const calculateMilestoneProgress = (currentTimestamp: number): number => {
  const milestoneTimestamps = getMilestoneTimestamps();
  let progress = 0; // 默认0个节点点亮

  for (let i = 0; i < milestoneTimestamps.length; i++) {
    if (currentTimestamp >= milestoneTimestamps[i]) {
      progress = i + 1;
    } else {
      break;
    }
  }

  return progress;
};

/**
 * 里程碑 Hook
 */
export function useMilestone(config: MilestoneConfig = {}) {
  const { serverTimestamp } = config;

  // ========== 状态管理 ==========

  // 当前达到的里程碑节点（0-5）
  const finishMilestone = ref(0);

  // ========== 核心功能函数 ==========

  /**
   * 获取当前时间并更新里程碑进度
   */
  const updateMilestoneProgress = async (): Promise<void> => {
    try {
      let currentTimestamp: number;

      // 使用 basic 接口的服务器时间戳（响应式）
      if (serverTimestamp && serverTimestamp.value > 0) {
        currentTimestamp = serverTimestamp.value;
      } else {
        // basic 接口时间戳未设置
        console.error('❌ basic 接口时间戳未设置，无法获取服务器时间');
        finishMilestone.value = 0;
        return;
      }

      // 计算并更新里程碑进度
      const progress = calculateMilestoneProgress(currentTimestamp);
      finishMilestone.value = progress;
    } catch (error) {
      console.error('❌ 获取时间失败:', error);
      // 发生错误时默认值为 0，所有节点都不点亮
      finishMilestone.value = 0;
    }
  };

  /**
   * 手动设置里程碑进度（用于测试）
   */
  const setMilestoneProgress = (progress: number): void => {
    if (progress >= 1 && progress <= 5) {
      finishMilestone.value = progress;
      console.log(`🎯 手动设置里程碑进度: ${progress}/5`);
    } else {
      console.warn('里程碑进度必须在 1-5 之间');
    }
  };

  /**
   * 获取里程碑时间节点信息
   */
  const getMilestoneInfo = () => {
    const timestamps = getMilestoneTimestamps();
    return timestamps.map((timestamp, index) => ({
      index: index + 1,
      timestamp,
      date: new Date(timestamp * 1000).toLocaleString('zh-CN', {
        timeZone: 'Asia/Shanghai',
      }),
    }));
  };

  // ========== 返回值 ==========
  return {
    // 状态
    finishMilestone,

    // 方法
    updateMilestoneProgress,
    setMilestoneProgress,
    getMilestoneInfo,

    // 工具函数
    calculateMilestoneProgress,
    getMilestoneTimestamps,
  };
}
